import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseService } from '../base/base.service';
import { SessionUserHashManagerService } from '../session-user-hash-manager/session-user-hash-manager.service';
import { EmailHistoryResult, EmailHistoryQuery, EmailHistorySummary } from '../../_models/admin/email-history.models';

/**
 * Typed, read-only calls to api/EmailHistory: the summary of a window and a page of the send log. The API
 * is admin-only and returns 404 while the admin.email-history switch is off.
 *
 * Follows the shared BaseService pattern: both calls are GETs with no body, so prepareOptions(null), which
 * also sets observe:'response', so responses are unwrapped.
 */
@Injectable({ providedIn: 'root' })
export class EmailHistoryService extends BaseService {

  /** The API's largest page. */
  public static readonly maxTake = 200;

  private apiUrl: string = environment.apiUrl;

  constructor(private http: HttpClient, protected hashManagerService: SessionUserHashManagerService) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
  }

  /** Counts for the last `hours` hours (1 to 720), by outcome and by process, and the newest problems. */
  public getSummary(hours: number): Observable<EmailHistorySummary> {
    const params = new HttpParams().set('hours', String(hours));
    return this.http.get(this.apiUrl + 'api/EmailHistory/Summary', { ...this.prepareOptions(null), params }).pipe(
      map((data: any) => this.unwrap(data) as EmailHistorySummary),
      catchError(this.handleError('getSummary'))
    );
  }

  /** One page of the send log, newest first. Filters left empty are not sent. */
  public search(query: EmailHistoryQuery): Observable<EmailHistoryResult> {
    let params = new HttpParams();
    (['fromUtc', 'toUtc', 'outcome', 'processKey', 'search'] as const).forEach(name => {
      const value = (query[name] ?? '').toString().trim();
      if (value) {
        params = params.set(name, value);
      }
    });
    params = params.set('skip', String(Math.max(0, query.skip)))
      .set('take', String(Math.min(EmailHistoryService.maxTake, Math.max(1, query.take))));
    return this.http.get(this.apiUrl + 'api/EmailHistory', { ...this.prepareOptions(null), params }).pipe(
      map((data: any) => this.unwrap(data) as EmailHistoryResult),
      catchError(this.handleError('search'))
    );
  }

  /** Shared body unwrap — prepareOptions() observes the full response, not just the body. */
  private unwrap(data: any): any {
    return data ? data.body : data;
  }
}
